import { useEffect, useRef, useState } from 'react'
import { Plus, Trash2, Pencil, Loader2, AlertTriangle, FileType, Check, X, ExternalLink, Upload } from 'lucide-react'
import { api } from '../lib/api.js'
import { CARD } from '../components/ui.jsx'
import { useConfirm } from '../components/ConfirmProvider.jsx'

/**
 * Settings → Static reference files
 *
 * Each entry is one PDF available in the SSO sections of every operation.
 * Staff manage: display name, the actual PDF file (upload / replace), and
 * which section(s) it shows up in (สสจ., ประกันสังคม, or both).
 *
 * Legacy entries committed to public/source-pdfs/ (e.g. sp_7, sp_19) still
 * appear here as "built-in" — name + section can be edited, and the file
 * can be REPLACED by uploading a new one (which moves storage to Wasabi).
 */
const SECTIONS = [
  { value: 'sso-provincial', label: 'สสจ.' },
  { value: 'sso-security',   label: 'ประกันสังคม' },
]
function sectionLabel(v) { return SECTIONS.find((s) => s.value === v)?.label || v }

export default function Settings() {
  const confirm = useConfirm()
  const [refs, setRefs]       = useState(null)
  const [err, setErr]         = useState(null)
  const [editing, setEditing] = useState(null)

  async function load() {
    try {
      const list = await api.staticRefs.list()
      setRefs(Array.isArray(list) ? list : [])
      setErr(null)
    } catch (e) { setErr(e.message); setRefs([]) }
  }
  useEffect(() => { load() }, [])

  async function removeRef(ref) {
    if (!await confirm({
      title: 'ลบเอกสารอ้างอิงนี้',
      message: <>เอกสาร <strong>{ref.name}</strong> จะถูกลบออกจาก {ref.categories.length === 2 ? 'ทั้ง 2 sections' : sectionLabel(ref.categories[0])} ของทุก Operation</>,
      confirmLabel: 'ลบ',
      tone: 'danger',
    })) return
    try { await api.staticRefs.remove(ref.key); load() }
    catch (e) { setErr(e.message) }
  }

  return (
    <div className="px-4 py-5 md:px-10 md:py-8 max-w-[1100px] mx-auto">
      <header className="mb-5">
        <h1 className="text-[22px] md:text-[26px] font-bold tracking-tight text-zinc-900">Settings</h1>
        <p className="text-[13px] text-zinc-500 mt-0.5">จัดการเอกสารอ้างอิง (static PDF) ที่จะแสดงในหมวด สสจ. / ประกันสังคม ของ Operation</p>
      </header>

      <div className={`${CARD} p-5 md:p-6`}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold text-zinc-900">เอกสารอ้างอิง</h3>
            <p className="text-[12px] text-zinc-500 mt-0.5">อัปโหลด PDF, ตั้งชื่อแสดงผล และเลือกหมวดที่จะให้แสดง</p>
          </div>
          <button onClick={() => setEditing({ __new: true })}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium shadow-sm shadow-amber-500/20">
            <Plus size={12} /> เพิ่มเอกสาร
          </button>
        </div>

        {err && (
          <div className="mb-3 px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-center gap-1.5">
            <AlertTriangle size={12} /> {err}
          </div>
        )}

        {refs == null ? (
          <div className="text-[12.5px] text-zinc-500 inline-flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> โหลด…</div>
        ) : refs.length === 0 ? (
          <p className="text-[12.5px] text-zinc-400 italic">ยังไม่มีเอกสารอ้างอิง</p>
        ) : (
          <ul className="divide-y divide-zinc-100 -mx-1">
            {refs.map((r) => (
              <li key={r.key} className="px-2 py-3 first:pt-1 last:pb-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileType size={14} className="text-rose-400 shrink-0" />
                    <div className="text-[13px] font-medium text-zinc-900 truncate">{r.name}</div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap sm:shrink-0">
                    {r.categories.map((c) => (
                      <span key={c} className="text-[10.5px] px-1.5 py-0.5 rounded-full font-medium bg-zinc-100 text-zinc-700">{sectionLabel(c)}</span>
                    ))}
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noreferrer"
                        title="เปิดดู" className="w-8 h-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100">
                        <ExternalLink size={12} />
                      </a>
                    )}
                    <button onClick={() => setEditing(r)} title="แก้ไข"
                      className="w-8 h-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => removeRef(r)} title="ลบ"
                      className="w-8 h-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <StaticRefEditor
          ref0={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}


function StaticRefEditor({ ref0, onClose, onSaved }) {
  const isNew = !!ref0.__new
  const [name, setName] = useState(isNew ? '' : ref0.name)
  const [cats, setCats] = useState(new Set(isNew ? [] : (ref0.categories || [])))
  const [file, setFile] = useState(null)              // File object selected by user (null = unchanged for existing)
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  function toggleCat(v) {
    setCats((prev) => {
      const n = new Set(prev)
      if (n.has(v)) n.delete(v); else n.add(v)
      return n
    })
  }

  function pickFile(f) {
    if (!f) { setFile(null); return }
    if (f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name || '')) {
      setErr('ไฟล์ต้องเป็น PDF')
      return
    }
    if (f.size > 25 * 1024 * 1024) {
      setErr(`ไฟล์ใหญ่เกินไป (${(f.size / 1024 / 1024).toFixed(1)} MB, สูงสุด 25 MB)`)
      return
    }
    setErr(null)
    setFile(f)
  }

  function onDrop(e) {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer?.files?.[0]
    if (f) pickFile(f)
  }
  function onDragOver(e) {
    // Required to allow drop. Also tells the browser this is a copy operation.
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    if (!dragOver) setDragOver(true)
  }
  function onDragLeave(e) {
    // Only clear when leaving the outer container (not crossing into a child).
    if (e.currentTarget.contains(e.relatedTarget)) return
    setDragOver(false)
  }

  function fileToDataUri(f) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload  = () => resolve(reader.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(f)
    })
  }

  async function save() {
    if (busy) return
    if (!name.trim())   { setErr('กรุณาใส่ชื่อเอกสาร'); return }
    if (cats.size === 0) { setErr('เลือก section อย่างน้อย 1 ที่'); return }
    if (isNew && !file) { setErr('กรุณาเลือกไฟล์ PDF'); return }
    setErr(null); setBusy(true)
    try {
      const body = { name: name.trim(), categories: [...cats] }
      if (file) {
        body.file     = await fileToDataUri(file)
        body.filename = file.name
      }
      if (isNew) await api.staticRefs.create(body)
      else       await api.staticRefs.update(ref0.key, body)
      onSaved()
    } catch (e) {
      setErr(e.message)
    } finally { setBusy(false) }
  }

  const currentFileLabel = file
    ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`
    : (isNew ? null : `${ref0.filename}${ref0.is_uploaded ? '' : ' (built-in)'}`)

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-zinc-900/40 backdrop-blur-[1px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[480px] flex flex-col">
        <header className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700 font-semibold">เอกสารอ้างอิง</div>
            <h2 className="text-[15px] font-bold text-zinc-900">{isNew ? 'เพิ่มใหม่' : 'แก้ไข'}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 inline-flex items-center justify-center">
            <X size={16} />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3.5">
          <label className="block">
            <span className="block mb-1 text-[11.5px] text-zinc-600 font-medium">ชื่อเอกสาร</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200}
              className="w-full px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[13px]"
              placeholder="เช่น สพ.7" />
          </label>

          <div>
            <span className="block mb-1 text-[11.5px] text-zinc-600 font-medium">
              {isNew ? 'ไฟล์ PDF' : 'ไฟล์ปัจจุบัน'}
            </span>
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className={
                'rounded-md ring-1 px-3 py-2.5 space-y-2 transition-colors ' +
                (dragOver
                  ? 'ring-amber-400 ring-2 bg-amber-50/70'
                  : 'ring-zinc-200 bg-zinc-50/60')
              }
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileType size={14} className={currentFileLabel ? 'text-rose-400 shrink-0' : 'text-zinc-300 shrink-0'} />
                {currentFileLabel ? (
                  <>
                    <span className="text-[12.5px] text-zinc-800 truncate min-w-0 flex-1">{currentFileLabel}</span>
                    {file && (
                      <button onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                        className="shrink-0 text-[11px] text-zinc-500 hover:text-rose-600 px-1.5 py-0.5 rounded">
                        ยกเลิก
                      </button>
                    )}
                  </>
                ) : (
                  <span className="text-[12px] text-zinc-400 italic">
                    {dragOver ? 'ปล่อยที่นี่เพื่ออัปโหลด' : 'ลากไฟล์มาวาง หรือกดปุ่มด้านล่าง'}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ring-1 ring-zinc-200 hover:bg-white text-[11.5px] text-zinc-700 shrink-0">
                  <Upload size={11} /> {isNew ? 'เลือกไฟล์' : 'อัปโหลดไฟล์ใหม่ (แทนของเดิม)'}
                </button>
                <span className="text-[10.5px] text-zinc-400">PDF · สูงสุด 25 MB</span>
              </div>
              <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          <div>
            <span className="block mb-1.5 text-[11.5px] text-zinc-600 font-medium">แสดงในหมวด</span>
            <div className="flex items-center gap-2">
              {SECTIONS.map((s) => {
                const on = cats.has(s.value)
                return (
                  <button key={s.value} type="button" onClick={() => toggleCat(s.value)}
                    className={
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium ' +
                      (on ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/20' : 'ring-1 ring-zinc-200 hover:bg-zinc-50 text-zinc-700')
                    }>
                    {on && <Check size={11} />} {s.label}
                  </button>
                )
              })}
            </div>
          </div>

          {err && (
            <div className="px-2.5 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12px] inline-flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{err}</span>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">ยกเลิก</button>
          <button onClick={save} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
            {busy && <Loader2 size={12} className="animate-spin" />}
            {isNew ? 'เพิ่ม' : 'บันทึก'}
          </button>
        </footer>
      </div>
    </div>
  )
}
